// ============================================================
// Auth Page â€” Register/Login: Email + Password | Reset: WA OTP atau Email Link
// ============================================================

import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import {
  preparePublicKeyOptions,
  credentialToJson,
  passkeySupported,
} from '../../helpers/webauthn.js';
import { clearUserClientState } from '../../utils/sessionCleanup.js';

function finalizeAuth(user, token) {
  clearUserClientState({ keepTheme: true });
  Alpine.store('auth').setAuth(user, token);
}

function normalizeLocalPhone(phone) {
  let digits = String(phone).replace(/\D/g, '');
  if (digits.startsWith('62')) digits = '0' + digits.slice(2);
  if (!digits.startsWith('0')) digits = '0' + digits;
  return digits;
}

export function registerLoginPage(routes = {}) {
  Alpine.data('loginPage', () => ({
    mode: 'login',       // login | register | forgot
    registerChannel: 'email', // email | phone
    registerOtpStep: 'phone', // phone | otp
    forgotStep: 'choose', // choose | phone | otp | password | email | email_sent
    forgotChannel: 'whatsapp', // whatsapp | email
    loginChannel: 'email',     // email | phone
    phoneLoginMode: 'otp',     // otp (WhatsApp) | password — default OTP (revisi #17)
    loginOtpStep: 'phone',     // phone | otp
    whatsappLoginEnabled: false,
    whatsappRegisterEnabled: false,
    emailResetEnabled: true,
    phone: '',
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    otpDigits: Array(6).fill(''),
    isLoading: false,
    countdown: 0,
    error: '',
    timer: null,
    passkeyEnabled: false,
    whatsappResetEnabled: true,
    showPass: false,
    showPassConfirm: false,
    showResetPass: false,
    showResetPassConfirm: false,
    routes,

    get otp() { return this.otpDigits.join(''); },
    get maskedPhone() {
      const p = normalizeLocalPhone(this.phone);
      return p.slice(0, 4) + '****' + p.slice(-4);
    },
    get maskedEmail() {
      const e = (this.email || '').trim();
      if (!e || !e.includes('@')) return '';
      const [local, domain] = e.split('@');
      return local.slice(0, 2) + '***@' + domain;
    },
    get canPasskey() {
      return this.passkeyEnabled && passkeySupported() && this.mode === 'login';
    },

    init() {
      if (window.location.pathname.includes('register')) {
        this.mode = 'register';
      }
      if (window.AUTH_ERROR) {
        this.error = window.AUTH_ERROR;
      }
      if (window.AUTH_STATUS === 'email-verified') {
        window.toast?.('Email berhasil diverifikasi! Silakan masuk.', 'success');
      }
      this.loadMethods();
    },

    async loadMethods() {
      try {
        const { data } = await axios.get('/api/v1/auth/methods');
        const methods = data.data || {};
        this.passkeyEnabled = !!methods.passkey;
        this.whatsappResetEnabled = methods.whatsapp_reset !== false;
        this.whatsappLoginEnabled = !!methods.whatsapp_login;
        this.whatsappRegisterEnabled = !!methods.whatsapp_register;
        if (this.whatsappLoginEnabled) {
          this.phoneLoginMode = 'otp';
          // Revisi #17: prefer tab WhatsApp OTP (bukan email) untuk login user
          if (this.mode === 'login') {
            this.loginChannel = 'phone';
          }
        } else {
          this.phoneLoginMode = 'password';
          this.loginOtpStep = 'phone';
        }
        if (this.mode === 'register' && this.whatsappRegisterEnabled) {
          this.registerChannel = 'phone';
        }
        this.emailResetEnabled = methods.email_reset !== false;
        if (!this.whatsappResetEnabled && this.emailResetEnabled) {
          this.forgotChannel = 'email';
        }
      } catch { /* ignore */ }
    },

    switchMode(next) {
      this.mode = next;
      this.forgotStep = 'choose';
      this.forgotChannel = this.whatsappResetEnabled ? 'whatsapp' : 'email';
      this.error = '';
      this.password = '';
      this.passwordConfirmation = '';
      this.showPass = false;
      this.showPassConfirm = false;
      this.showResetPass = false;
      this.showResetPassConfirm = false;
      this.loginChannel = (next === 'login' && this.whatsappLoginEnabled) ? 'phone' : 'email';
      this.phoneLoginMode = this.whatsappLoginEnabled ? 'otp' : 'password';
      this.loginOtpStep = 'phone';
      this.registerChannel = (next === 'register' && this.whatsappRegisterEnabled) ? 'phone' : 'email';
      this.registerOtpStep = 'phone';
      this.otpDigits = Array(6).fill('');
    },

    /** Nomor HP login: default OTP WhatsApp bila aktif. */
    setLoginChannel(channel) {
      this.loginChannel = channel;
      this.error = '';
      this.loginOtpStep = 'phone';
      this.otpDigits = Array(6).fill('');
      if (channel === 'phone') {
        this.phoneLoginMode = this.whatsappLoginEnabled ? 'otp' : 'password';
      }
    },

    setPhoneLoginMode(mode) {
      this.phoneLoginMode = mode;
      this.loginOtpStep = 'phone';
      this.error = '';
      this.otpDigits = Array(6).fill('');
    },

    /** Prefer this over bare assignment in Blade — avoids Alpine free-var ReferenceError. */
    setRegisterChannel(channel) {
      this.registerChannel = channel;
      this.registerOtpStep = 'phone';
      this.error = '';
      this.otpDigits = Array(6).fill('');
    },

    resetRegisterOtpStep() {
      this.registerOtpStep = 'phone';
      this.error = '';
      this.otpDigits = Array(6).fill('');
    },

    resetLoginOtpStep() {
      this.loginOtpStep = 'phone';
      this.error = '';
      this.otpDigits = Array(6).fill('');
    },

    selectForgotChannel(channel) {
      this.forgotChannel = channel;
      this.error = '';
      this.forgotStep = channel === 'whatsapp' ? 'phone' : 'email';
    },

    async requestResetEmail() {
      const email = (this.email || '').trim();
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        this.error = 'Email tidak valid.';
        return;
      }
      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/forgot-password/request-email', { email });
        this.forgotStep = 'email_sent';
        this.startCountdown(60);
        window.toast?.(data.message || 'Link reset dikirim ke email.', 'success');
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal mengirim link reset.';
      } finally {
        this.isLoading = false;
      }
    },

    roleNames(user) {
      const raw = user?.roles;
      if (!Array.isArray(raw)) return [];
      return raw
        .map((r) => (typeof r === 'string' ? r : r?.name))
        .filter(Boolean);
    },

    async redirectAfterLogin(user, meta = {}) {
      const roles = this.roleNames(user);

      // Admin / kasir: bukan portal pelanggan
      if (roles.some((r) => ['admin', 'super_admin', 'kasir'].includes(r))) {
        Alpine.store('auth').clearAuth();
        this.error = 'Halaman login ini hanya untuk pelanggan. Gunakan portal yang sesuai dengan akun Anda.';
        return;
      }

      // Creator: arahkan ke Studio (sesi panel via bridge token Sanctum)
      if (roles.includes('creator')) {
        try {
          const { data } = await axios.post('/studio/login/bridge', {});
          const target = data?.data?.redirect || this.routes?.creator || '/studio/dashboard';
          window.toast?.('Selamat datang di Creator Studio!', 'success');
          window.location.href = target;
        } catch (e) {
          window.location.href = this.routes?.creator || '/studio/login';
        }
        return;
      }

      const needsVerify = meta.email_verification_sent
        || (user?.email && !user?.email_verified_at);
      if (needsVerify) {
        window.toast?.('Link verifikasi dikirim ke email Anda. Cek inbox atau spam.', 'info');
        window.location.href = '/email/verify';
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
        window.location.href = redirect;
        return;
      }
      setTimeout(() => { window.location.href = '/dashboard'; }, 800);
    },

    async submitRegister() {
      if (this.registerChannel === 'phone' && this.whatsappRegisterEnabled) {
        return this.submitRegisterOtp();
      }

      const email = (this.email || '').trim();
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        this.error = 'Email tidak valid.';
        return;
      }
      if (this.password.length < 8) {
        this.error = 'Password minimal 8 karakter.';
        return;
      }
      if (this.password !== this.passwordConfirmation) {
        this.error = 'Konfirmasi password tidak cocok.';
        return;
      }

      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/register', {
          email,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
          referral_code: new URLSearchParams(window.location.search).get('ref') || undefined,
        });
        const payload = data.data;
        finalizeAuth(payload.user, payload.token);
        if (payload.email_verification_sent) {
          window.toast?.('Akun berhasil dibuat! Cek email untuk verifikasi.', 'success');
        } else {
          window.toast?.('Akun berhasil dibuat!', 'success');
        }
        await this.maybeRegisterPasskey();
        await this.redirectAfterLogin(payload.user, payload);
      } catch (e) {
        this.error = e.response?.data?.message || 'Registrasi gagal.';
      } finally {
        this.isLoading = false;
      }
    },

    async requestRegisterOtp() {
      const localPhone = normalizeLocalPhone(this.phone);
      if (!localPhone || localPhone.length < 10) {
        this.error = 'Nomor HP tidak valid.';
        return;
      }
      this.phone = localPhone;
      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/register-otp/request', {
          phone: localPhone,
          channel: 'whatsapp',
        });
        window.toast?.(data.message || 'OTP dikirim ke WhatsApp.', 'success');
        this.registerOtpStep = 'otp';
        this.otpDigits = Array(6).fill('');
        this.startCountdown(60);
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal mengirim OTP.';
      } finally {
        this.isLoading = false;
      }
    },

    async submitRegisterOtp() {
      if (this.registerOtpStep === 'phone') {
        return this.requestRegisterOtp();
      }
      if (this.otp.length !== 6) {
        this.error = 'Masukkan 6 digit OTP.';
        return;
      }

      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/register-otp/verify', {
          phone: this.phone,
          otp_code: this.otp,
        });
        const payload = data.data;
        finalizeAuth(payload.user, payload.token);
        window.toast?.(data.message || 'Registrasi berhasil!', 'success');
        await this.redirectAfterLogin(payload.user, payload);
      } catch (e) {
        this.error = e.response?.data?.message || 'OTP tidak valid.';
      } finally {
        this.isLoading = false;
      }
    },

    async submitLogin() {
      if (this.loginChannel === 'phone' && this.phoneLoginMode === 'password') {
        return this.submitPhoneLogin();
      }
      if (this.loginChannel === 'phone' && this.phoneLoginMode === 'otp') {
        return this.submitLoginOtp();
      }

      const email = (this.email || '').trim();
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        this.error = 'Email tidak valid.';
        return;
      }
      if (!this.password) {
        this.error = 'Password wajib diisi.';
        return;
      }

      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/login-password', {
          login: email,
          password: this.password,
        });
        const payload = data.data;
        finalizeAuth(payload.user, payload.token);
        window.toast?.('Selamat datang, ' + payload.user.name + '!', 'success');
        await this.redirectAfterLogin(payload.user);
      } catch (e) {
        this.error = e.response?.data?.message || 'Email atau password salah.';
      } finally {
        this.isLoading = false;
      }
    },

    async submitPhoneLogin() {
      const localPhone = normalizeLocalPhone(this.phone);
      if (!localPhone || localPhone.length < 10) {
        this.error = 'Nomor HP tidak valid.';
        return;
      }
      if (!this.password) {
        this.error = 'Password wajib diisi.';
        return;
      }

      this.phone = localPhone;
      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/login', {
          phone: localPhone,
          password: this.password,
        });
        const payload = data.data;
        finalizeAuth(payload.user, payload.token);
        window.toast?.('Selamat datang, ' + payload.user.name + '!', 'success');
        await this.redirectAfterLogin(payload.user);
      } catch (e) {
        this.error = e.response?.data?.message || 'Nomor HP atau password salah.';
      } finally {
        this.isLoading = false;
      }
    },

    async requestLoginOtp() {
      const localPhone = normalizeLocalPhone(this.phone);
      if (!localPhone || localPhone.length < 10) {
        this.error = 'Nomor HP tidak valid.';
        return;
      }
      this.phone = localPhone;
      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/login-otp/request', { phone: localPhone });
        const otpSent = data.data?.otp_sent !== false;
        window.toast?.(data.message || (otpSent ? 'OTP dikirim.' : 'Permintaan diproses.'), otpSent ? 'success' : 'info');
        if (!otpSent) return;
        this.loginOtpStep = 'otp';
        this.otpDigits = Array(6).fill('');
        this.startCountdown(60);
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal mengirim OTP.';
      } finally {
        this.isLoading = false;
      }
    },

    async submitLoginOtp() {
      if (this.loginOtpStep === 'phone') {
        return this.requestLoginOtp();
      }
      if (this.otp.length !== 6) {
        this.error = 'Masukkan 6 digit OTP.';
        return;
      }

      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/login-otp/verify', {
          phone: this.phone,
          otp_code: this.otp,
        });
        const payload = data.data;
        finalizeAuth(payload.user, payload.token);
        window.toast?.('Login berhasil!', 'success');
        await this.redirectAfterLogin(payload.user);
      } catch (e) {
        this.error = e.response?.data?.message || 'OTP tidak valid.';
      } finally {
        this.isLoading = false;
      }
    },

    async loginWithPasskey() {
      if (!this.canPasskey) {
        this.error = 'Passkey tidak didukung di browser ini.';
        return;
      }

      const localPhone = this.phone ? normalizeLocalPhone(this.phone) : '';
      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/passkey/login/options', {
          phone: localPhone || null,
        });

        const session = data.data.session;
        const options = data.data.options;
        options.publicKey = preparePublicKeyOptions(options.publicKey);

        const credential = await navigator.credentials.get(options);
        if (!credential) throw new Error('Passkey dibatalkan.');

        const body = credentialToJson(credential);
        const loginRes = await axios.post('/api/v1/auth/passkey/login', {
          session,
          ...body,
        });

        const payload = loginRes.data.data;
        finalizeAuth(payload.user, payload.token);
        window.toast?.('Login passkey berhasil!', 'success');
        await this.redirectAfterLogin(payload.user);
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Login passkey gagal.';
      } finally {
        this.isLoading = false;
      }
    },

    async maybeRegisterPasskey() {
      if (!this.canPasskey || !this.password) return;
      try {
        const { data } = await axios.post('/api/v1/auth/passkey/register/options', {
          password: this.password,
        });
        const options = data.data.options;
        options.publicKey = preparePublicKeyOptions(options.publicKey);
        const credential = await navigator.credentials.create(options);
        if (!credential) return;
        await axios.post('/api/v1/auth/passkey/register', {
          password: this.password,
          ...credentialToJson(credential),
        });
        window.toast?.('Passkey tersimpan â€” login lebih cepat.', 'success');
      } catch { /* opsional */ }
    },

    async requestResetOtp() {
      const localPhone = normalizeLocalPhone(this.phone);
      if (!localPhone || localPhone.length < 10) {
        this.error = 'Nomor HP tidak valid.';
        return;
      }
      this.phone = localPhone;
      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/forgot-password/request-otp', {
          phone: localPhone,
        });

        const otpSent = data.data?.otp_sent !== false;
        window.toast?.(data.message || (otpSent ? 'OTP dikirim ke WhatsApp.' : 'Permintaan diproses.'), otpSent ? 'success' : 'info');

        if (!otpSent) {
          return;
        }

        this.forgotStep = 'otp';
        this.otpDigits = Array(6).fill('');
        this.startCountdown(60);
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal mengirim OTP.';
      } finally {
        this.isLoading = false;
      }
    },

    goToResetPassword() {
      if (this.otp.length !== 6) {
        this.error = 'Masukkan 6 digit OTP.';
        return;
      }
      this.forgotStep = 'password';
      this.error = '';
      this.password = '';
      this.passwordConfirmation = '';
      this.showResetPass = false;
      this.showResetPassConfirm = false;
    },

    async submitResetPassword() {
      if (this.password.length < 8) {
        this.error = 'Password minimal 8 karakter.';
        return;
      }
      if (this.password !== this.passwordConfirmation) {
        this.error = 'Konfirmasi password tidak cocok.';
        return;
      }

      this.isLoading = true;
      this.error = '';

      try {
        const { data } = await axios.post('/api/v1/auth/forgot-password/reset', {
          phone: this.phone,
          otp_code: this.otp,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
        });
        window.toast?.('Password diperbarui. Masuk dengan nomor HP + password baru.', 'success');
        const savedPhone = this.phone;
        this.switchMode('login');
        this.loginChannel = 'phone';
        this.phoneLoginMode = 'password';
        this.phone = savedPhone;
        this.password = '';
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal reset password.';
      } finally {
        this.isLoading = false;
      }
    },

    startCountdown(seconds) {
      this.countdown = seconds;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        if (--this.countdown <= 0) clearInterval(this.timer);
      }, 1000);
    },

    handleOtpInput(index, event, context = 'forgot') {
      const val = event.target.value.replace(/\D/, '');
      this.otpDigits[index] = val ? val.slice(-1) : '';
      if (val && index < 5) {
        const attr = context === 'login' ? 'data-login-otp' : (context === 'register' ? 'data-reg-otp' : 'data-otp');
        this.$nextTick(() => document.querySelector(`[${attr}="${index + 1}"]`)?.focus());
      }
    },

    handleOtpKey(index, event, context = 'forgot') {
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        const attr = context === 'login' ? 'data-login-otp' : (context === 'register' ? 'data-reg-otp' : 'data-otp');
        this.$nextTick(() => document.querySelector(`[${attr}="${index - 1}"]`)?.focus());
      }
    },
  }));
}
